import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  Grid,
  IconButton,
  Select,
  MenuItem,
  Skeleton,
  useTheme,
  useMediaQuery,
  Pagination
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import FinFactorBankCard from "../../components/common/cards/FinFactorBankCard";
import AppCardComponent from "../../components/mui/AppCardComponent";
import WealthChart from "../../components/chart/WealthChart";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useGetInvestmentPortfolioStocksDetailsQuery, useGetInvestmentPortfolioEtfDetailsQuery, useGetInvestmentPortfolioInvitDetailsQuery, useGetInvestmentPortfolioReitDetailsQuery } from "../../store/api/finfactor.apislice";
import { isNative } from "../../utils/platform.utils";
import { getLogoUrl, getFundInitial } from "../../utils/getLogoUrl";

import NodisplayComponent from "../../components/common/NodisplayComponent";

const StockHoldingRow = ({ stock, showClosingPrice = true, isReit = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderRadius: "16px",
        p: { xs: 1.5, md: 2 },
        border: "1px solid rgba(255, 255, 255, 0.05)",
      }}
    >
      <Grid container spacing={{ xs: 1.5, md: 0 }} alignItems="center">
        {/* Left Section: Name and ISIN */}
        <Grid item xs={7} md={isReit ? 4 : 4.5}>
          <Box>
            <Typography
              variant="body1"
              fontWeight="600"
              color="white"
              sx={{
                fontSize: { xs: "0.9rem", md: "1rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {stock.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: { xs: "10px", md: "12px" } }}
            >
              {stock.isin}
            </Typography>
          </Box>
        </Grid>

        {/* Middle Section: Value and Change (Top Right on Mobile) */}
        <Grid item xs={5} md={isReit ? 3 : 3.5}>
          <Box sx={{ textAlign: "right", pr: { xs: 0, md: isReit ? 4 : 2 } }}>
            <Typography
              variant="body1"
              fontWeight="700"
              color="white"
              sx={{ fontSize: { xs: "0.9rem", md: "1.1rem" } }}
            >
              {stock.value}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={0.5}
            >
              {parseFloat(String(stock.dayChange).replace(/[₹,]/g, "")) !==
                0 &&
                (stock.isPositive ? (
                  <ArrowDropUpIcon sx={{ fontSize: { xs: 16, md: 18 }, color: "#10B981" }} />
                ) : (
                  <ArrowDropDownIcon sx={{ fontSize: { xs: 16, md: 18 }, color: "#EF4444" }} />
                ))}
              <Typography
                variant="caption"
                sx={{
                  color:
                    (stock.change === "-" || parseFloat(String(stock.dayChange).replace(/[₹,]/g, "")) === 0 || stock.isPositive === null)
                      ? "white"
                      : stock.isPositive
                        ? "#10B981"
                        : "#EF4444",
                  fontWeight: 600,
                }}
              >
                {(() => {
                  const changeText = String(stock.change);
                  if (changeText.endsWith("1D")) {
                    const valuePart = changeText.substring(0, changeText.length - 2);
                    return (
                      <>
                        {valuePart}
                        <Typography
                          component="span"
                          variant="inherit"
                          sx={{ color: "white", fontWeight: 400, ml: 0.5 }}
                        >
                          1D
                        </Typography>
                      </>
                    );
                  }
                  return changeText;
                })()}
              </Typography>
            </Stack>
          </Box>
        </Grid>

        {/* Vertical Separator (Hidden on Mobile) */}
        {!isMobile && (
          <Grid item xs={0.5} sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                height: 32,
                borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            />
          </Grid>
        )}

        {/* Analytical Section: Price and Qty (Bottom Row on Mobile) */}
        <Grid item xs={12} md={isReit ? 4.5 : 3.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pl: { xs: 0, md: 1 },
              pt: { xs: 1.5, md: 0 },
              mt: { xs: 0.5, md: 0 },
              borderTop: { xs: "1px solid rgba(255, 255, 255, 0.1)", md: "none" },
            }}
          >
            {isReit ? (
              <>
                <Box>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                  >
                    LTP
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="white" fontSize={{ xs: "13px", md: "14px" }}>
                    {stock.closingPrice}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                  >
                    Qty.
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="white" fontSize={{ xs: "13px", md: "14px" }}>
                    {stock.qty}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                {showClosingPrice && (
                  <Box>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                    >
                      Closing Price
                    </Typography>
                    <Typography variant="body1" fontWeight="600" color="white" fontSize={{ xs: "13px", md: "14px" }}>
                      {stock.closingPrice}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ textAlign: "right", ml: "auto" }}>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                  >
                    Qty.
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="white" fontSize={{ xs: "13px", md: "14px" }}>
                    {stock.qty}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const FinStockDetailContainer = () => {
  const { category, brokerId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isStocks = category === "stocks";
  const isEtf = category === "etf";
  const isInvit = category === "invit";
  const isReit = category === "reit";

  const {
    data: stockDetails,
    isLoading: isStocksLoading,
    isError: isStocksError,
  } = useGetInvestmentPortfolioStocksDetailsQuery(brokerId, {
    skip: !isStocks,
  });

  const {
    data: etfDetails,
    isLoading: isEtfLoading,
    isError: isEtfError,
  } = useGetInvestmentPortfolioEtfDetailsQuery(brokerId, {
    skip: !isEtf,
  });

  const {
    data: invitDetails,
    isLoading: isInvitLoading,
    isError: isInvitError,
  } = useGetInvestmentPortfolioInvitDetailsQuery(brokerId, {
    skip: category !== "invit",
  });

  const {
    data: reitDetails,
    isLoading: isReitLoading,
    isError: isReitError,
  } = useGetInvestmentPortfolioReitDetailsQuery(brokerId, {
    skip: category !== "reit",
  });

  const broker = isStocks ? stockDetails : isEtf ? etfDetails : isInvit ? invitDetails : reitDetails;
  const isLoading = isStocks
    ? isStocksLoading
    : isEtf
      ? isEtfLoading
      : isInvit
        ? isInvitLoading
        : isReitLoading;

  const isError = isStocks ? isStocksError : isEtf ? isEtfError : isInvit ? isInvitError : isReitError;

  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container
          maxWidth="lg"
          sx={{ px: { xs: 1.5, sm: 2 }, mt: 2 }}
          disableGutters
        >
          <Skeleton
            variant="rectangular"
            height={140}
            sx={{
              borderRadius: "24px",
              mb: 3,
              bgcolor: "rgba(255,255,255,0.05)",
            }}
          />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                height={360}
                sx={{ borderRadius: "24px", bgcolor: "rgba(255,255,255,0.05)" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                height={360}
                sx={{ borderRadius: "24px", bgcolor: "rgba(255,255,255,0.05)" }}
              />
            </Grid>
          </Grid>
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: "24px", bgcolor: "rgba(255,255,255,0.05)" }}
          />
        </Container>
      </Box>
    );
  }

  if (isError || !broker) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          pb: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <NodisplayComponent
          title="No Details Found"
          subtitle={isError ? "We couldn't retrieve the portfolio details at this time. Please try again later." : "No holdings found for this broker."}
        />
      </Box>
    );
  }


  const marketCapData = broker.marketCap?.data || [];
  const sectorData = broker.sector?.data || [];
  const stockHoldings = broker.holdings || [];
  const totalRows = stockHoldings.length;

  const formatChartCategoryLabel = (label = "") => {
    return String(label)
      .replace(/\s*-\s*\d+(\.\d+)?%\s*$/i, "")
      .trim();
  };

  const marketCapChartData = marketCapData.map((item) => ({
    ...item,
    category: formatChartCategoryLabel(item?.category),
  }));

  const sectorChartData = sectorData.map((item) => ({
    ...item,
    category: formatChartCategoryLabel(item?.category),
  }));

  const displayedHoldings = stockHoldings.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }} disableGutters>
        {/* Top Card */}
        <FinFactorBankCard
          logo={getLogoUrl(broker)}
          fallbackIcon={getFundInitial(broker)}
          accountNumber={
            <Box>
              <Typography
                variant="body1"
                fontWeight="600"
                color="white"
                sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
              >
                {broker.name}
              </Typography>
              {broker.accountId && (
                <Typography
                  variant="caption"
                  color="white"
                  sx={{ fontSize: "12px", opacity: 0.7 }}
                >
                  {broker.accountId}
                </Typography>
              )}
            </Box>
          }
          nomineeStatus={broker.nomineeStatus || "not registered"}
          balance={
            <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={0.5}>
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="#38bdf8"
                sx={{ fontSize: { xs: "14px", md: "16px" } }}
              >
                {broker.value}
              </Typography>
              {broker.changeValue && broker.changeValue !== "-" && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {Number(broker.rawChangePercent) !== 0 && (
                    broker.isPositive ? (
                      <ArrowDropUpIcon
                        sx={{ fontSize: { xs: 16, md: 18 }, color: "#10B981" }}
                      />
                    ) : (
                      <ArrowDropDownIcon
                        sx={{ fontSize: { xs: 16, md: 18 }, color: "#EF4444" }}
                      />
                    )
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: (broker.changeValue === "-" || Number(broker.rawChangePercent) === 0 || broker.isPositive === null) ? "white" : (broker.isPositive ? "#10B981" : "#EF4444"),
                      fontWeight: 600,
                      fontSize: { xs: "11px", md: "12px" },
                    }}
                  >
                    <span
                      style={{
                        color: (broker.changeValue === "-" || Number(broker.rawChangePercent) === 0 || broker.isPositive === null) ? "white" : (broker.isPositive ? "#10B981" : "#EF4444"),
                      }}
                    >
                      {broker.changeValue}
                    </span>{" "}
                    <span style={{ color: "#fff" }}>{broker.changeLabel}</span>
                  </Typography>
                </Stack>
              )}
            </Stack>
          }
          showChevron={false}
          backgroundColor="rgba(255, 255, 255, 0.03)"
          sx={{
            mb: 3,
            mt: 1,
            border: "1px solid rgba(255, 255, 255, 0.05)",
            "& .MuiAvatar-root": { width: 44, height: 44 },
          }}
        />

        {/* Charts Section */}
        {((marketCapData && marketCapData.length > 0) ||
          (sectorData && sectorData.length > 0)) && (
            <Grid container spacing={{ xs: 2.5, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
              <Grid item xs={12} md={6}>
                <AppCardComponent
                  title="Market Cap distribution"
                  sx={{
                    bgcolor: "#13172a",
                    borderRadius: "24px",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    height: "100%",
                    "& .MuiCardHeader-title": {
                      color: "white",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                    },
                    p: 1,
                    "& .MuiCardHeader-root": {
                      pb: { xs: 0, md: 2 },
                    },
                    "& .MuiCardContent-root": {
                      pt: { xs: 0, md: 2 },
                      pb: { xs: 0, md: 2 },
                    }
                  }}
                >
                  <WealthChart
                    data={marketCapChartData}
                    centerLabel="Total Amount"
                    centerValue={broker.marketCap?.totalAmount || "₹0"}
                    chartSize={isXs ? 320 : (isMobile ? 340 : 380)}
                    padding={{ top: 0, bottom: 0, left: 30, right: 30 }}
                    centerValueFontSize={isNative() ? (isXs ? 18 : 22) : (isXs ? 22 : 34)}
                    centerValueFontWeight={600}
                    centerLabelFontSize={isNative() ? (isXs ? 11 : 13) : (isXs ? 13 : 18)}
                    centerSubLabelFontSize={isNative() ? (isXs ? 10 : 11) : (isXs ? 11 : 12)}
                    legendFontSize={isXs ? 13 : 12}
                    toggleLabelsOnMobile={true}
                    legendPosition="bottom"
                    labelFontSize={isMobile ? (isXs ? 9 : 11) : 16}
                  />
                </AppCardComponent>
              </Grid>
              <Grid item xs={12} md={6}>
                <AppCardComponent
                  title="Sector-wise bifurcation"
                  sx={{
                    bgcolor: "#13172a",
                    borderRadius: "24px",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    height: "100%",
                    "& .MuiCardHeader-title": {
                      color: "white",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                    },
                    p: 1,
                    "& .MuiCardContent-root": {
                      pt: { xs: 0, md: 2 },
                      pb: { xs: 0, md: 2 },
                    }
                  }}
                >
                  <WealthChart
                    data={sectorChartData}
                    centerLabel="Total Amount"
                    centerValue={broker.sector?.totalAmount || "₹0"}
                    chartSize={isXs ? 400 : (isMobile ? 480 : 380)}
                    padding={{ top: 0, bottom: 0, left: 30, right: 30 }}
                    centerValueFontSize={isNative() ? (isXs ? 18 : 22) : (isXs ? 22 : 34)}
                    centerValueFontWeight={600}
                    centerLabelFontSize={isNative() ? (isXs ? 11 : 13) : (isXs ? 13 : 18)}
                    centerSubLabelFontSize={isNative() ? (isXs ? 10 : 11) : (isXs ? 11 : 12)}
                    legendFontSize={isXs ? 13 : 12}
                    toggleLabelsOnMobile={true}
                    legendPosition="bottom"
                    labelFontSize={isMobile ? (isXs ? 9 : 11) : 16}
                  />
                </AppCardComponent>
              </Grid>
            </Grid>
          )}

        {/* Stock List Section */}
        <AppCardComponent
          sx={{
            bgcolor: "#13172a",
            borderRadius: "24px",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            p: 1,
            "& .MuiCardHeader-root": { display: "none" },
          }}
        >
          <Stack spacing={2} sx={{}}>
            {displayedHoldings.map((stock, index) => (
              <StockHoldingRow
                key={index}
                stock={stock}
                showClosingPrice={!(isInvit || isReit)}
                isReit={isReit}
              />
            ))}

            {/* Pagination Footer */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                pt: 3,
                mt: 1,
                gap: { xs: 3, sm: 0 }
              }}
            >
              <Typography
                sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "14px" }}
              >
                {(page - 1) * rowsPerPage + 1}-
                {Math.min(page * rowsPerPage, totalRows)} of {totalRows} rows
              </Typography>

              <Pagination
                count={Math.ceil(totalRows / rowsPerPage)}
                page={page}
                onChange={(e, p) => setPage(p)}
                siblingCount={isXs ? 0 : 1}
                boundaryCount={isXs ? 1 : 1}
                size={isXs ? "small" : "medium"}
                showFirstButton={!isXs}
                showLastButton={!isXs}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "rgba(255, 255, 255, 0.6)",
                    backgroundColor: "transparent",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "6px",
                    minWidth: { xs: "32px", md: "36px" },
                    height: { xs: "32px", md: "36px" },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#55B4E3",
                      color: "white",
                      border: "none",
                      "&:hover": {
                        backgroundColor: "#55B4E3",
                      }
                    },
                    "& .MuiPaginationItem-icon": {
                      fontSize: { xs: "18px", md: "20px" },
                      color: "rgba(255, 255, 255, 0.6)"
                    }
                  }
                }}
              />

              <Box>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.03)",
                    fontSize: "14px",
                    height: "40px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "& .MuiSvgIcon-root": { color: "white" },
                    "& .MuiSelect-select": { py: 0 },
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      sx: {
                        mt: 0.75,
                        backgroundColor: "#13172a",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        borderRadius: "8px",
                        "& .MuiMenuItem-root": {
                          color: "white",
                          textDecoration: "none",
                          fontSize: "14px",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "rgba(56, 189, 248, 0.2)",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "rgba(56, 189, 248, 0.28)",
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                </Select>
              </Box>
            </Box>
          </Stack>
        </AppCardComponent>
      </Container>
    </Box>
  );
};

export default FinStockDetailContainer;
